import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'
import { ShieldIcon, CloseIcon } from './icons.js'

export * from './icons.js'

/**
 * Primitive UI dùng chung.
 *
 * Nguyên tắc: không component nào ở đây biết partner là ai, đang ở step nào, hay
 * container là gì. Chúng nhận props và vẽ. Mọi quyết định nghiệp vụ nằm ở `@esign/steps`.
 * Vì thế package này KHÔNG phụ thuộc `@esign/contracts` — không có cách nào lén đọc
 * `context.partner.id` từ trong đây.
 */

// ─── Button ───────────────────────────────────────────────────────────────────

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'link'
}

export function Button({ variant = 'primary', className, ...rest }: ButtonProps) {
  const variantClass = variant === 'primary' ? '' : ` btn--${variant}`
  return <button type="button" className={`btn${variantClass}${className ? ` ${className}` : ''}`} {...rest} />
}

// ─── Field ────────────────────────────────────────────────────────────────────

type FieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> & {
  label: string
  hint?: string
  error?: string
  icon?: ReactNode
  action?: ReactNode
  onValueChange?: (value: string) => void
}

export function Field({ label, hint, error, icon, action, onValueChange, className, ...rest }: FieldProps) {
  const inputEl = (
    <input
      className={`field__input${icon ? ' field__input--with-icon' : ''}${error ? ' field__input--error' : ''}${className ? ` ${className}` : ''}`}
      onChange={(e) => onValueChange?.(e.target.value)}
      {...rest}
      {...(rest.readOnly ? { 'aria-readonly': true } : {})}
    />
  )

  const inputWithIcon = icon ? (
    <div className="field__input-wrap">
      <span className="field__icon">{icon}</span>
      {inputEl}
    </div>
  ) : (
    inputEl
  )

  return (
    <label className={`field${error ? ' field--error' : ''}`}>
      <span className="field__label">{label}</span>
      {action ? (
        <span className="field__row">
          {inputWithIcon}
          {action}
        </span>
      ) : (
        inputWithIcon
      )}
      {error ? (
        <span className="field__error">{error}</span>
      ) : (
        hint && <span className="field__hint">{hint}</span>
      )}
    </label>
  )
}

// ─── BankCard ─────────────────────────────────────────────────────────────────

export function BankCard({
  bankName,
  holderName,
  accountNumber,
  onAction,
}: {
  bankName: string
  holderName: string
  accountNumber: string
  onAction?: () => void
}) {
  return (
    <div className="bank-card-container">
      <span className="field__label">Thẻ ngân hàng</span>
      <div className="bank-card">
        <div className="bank-card__info">
          <span className="bank-card__name">{bankName}</span>
          <span className="bank-card__holder">{holderName}</span>
          <span className="bank-card__number">Số tài khoản: {accountNumber}</span>
        </div>
        {onAction && (
          <button type="button" className="bank-card__action" onClick={onAction}>
            Đổi tài khoản
          </button>
        )}
      </div>
    </div>
  )
}

// ─── Card ─────────────────────────────────────────────────────────────────────

export const Card = ({ title, children, className }: { title?: string; children: ReactNode; className?: string }) => (
  <section className={`card${className ? ` ${className}` : ''}`}>
    {title && <h2 className="card__title">{title}</h2>}
    {children}
  </section>
)

// ─── Alert ────────────────────────────────────────────────────────────────────

export const Alert = ({
  tone = 'danger',
  children,
}: {
  tone?: 'danger' | 'info' | 'success' | 'warning'
  children: ReactNode
}) => (
  <div className={`alert${tone === 'danger' ? '' : ` alert--${tone}`}`} role="alert">
    {children}
  </div>
)

export const Note = ({ children }: { children: ReactNode }) => <p className="note">{children}</p>

export const SecurityNote = ({ text = 'Thông tin của bạn được hoàn toàn bảo mật, chúng tôi không chia sẻ với bên thứ 3' }: { text?: string }) => (
  <div className="security-note">
    <ShieldIcon className="security-note__icon" />
    <span>{text}</span>
  </div>
)

// ─── Stepper ──────────────────────────────────────────────────────────────────

export type StepperItem = { key: string; label: string; state: 'done' | 'current' | 'todo' }

export const Stepper = ({ items }: { items: StepperItem[] }) => (
  <nav className="stepper" aria-label="Tiến trình ký">
    {items.map((item, index) => (
      <div
        key={item.key}
        className={`stepper__item${item.state === 'todo' ? '' : ` stepper__item--${item.state}`}`}
        {...(item.state === 'current' ? { 'aria-current': 'step' as const } : {})}
      >
        <div className="stepper__node">
          {index > 0 && <div className={`stepper__line stepper__line--prev${item.state !== 'todo' ? ' stepper__line--active' : ''}`} />}
          <div className="stepper__dot">
            <div className="stepper__dot-inner" />
          </div>
          {index < items.length - 1 && <div className={`stepper__line stepper__line--next${item.state === 'done' ? ' stepper__line--active' : ''}`} />}
        </div>
        <span className="stepper__label">{item.label}</span>
      </div>
    ))}
  </nav>
)

// ─── Sheet / Modal ────────────────────────────────────────────────────────────

export const Sheet = ({
  title,
  body,
  icon,
  onClose,
  className,
  children,
}: {
  title: string
  body?: ReactNode
  icon?: ReactNode
  onClose?: () => void
  className?: string
  children: ReactNode
}) => (
  <div className={`sheet${className ? ` ${className}` : ''}`} role="dialog" aria-modal="true" aria-label={title}>
    <div className="sheet__panel">
      {onClose && (
        <button type="button" className="sheet__close" onClick={onClose} aria-label="Đóng">
          <CloseIcon />
        </button>
      )}
      {icon && <div className="sheet__icon-wrap">{icon}</div>}
      <h2 className="sheet__title">{title}</h2>
      {body && <div className="sheet__body">{body}</div>}
      <div className="sheet__actions">{children}</div>
    </div>
  </div>
)
